import { fullLabel } from "../components/WhereItIs";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { scanAccept, scanRefuse, scanSoundOnly } from "@/lib/scanFeedback";
import { NoPhotoBypassDialog } from "@/components/NoPhotoBypass";
import { SoundToggle } from "@/components/SoundToggle";
import { ScanField } from "@/components/ScanField";
import {
  ArrowRightLeft, Send, PackageCheck, ScanLine, MapPin, Building2, CheckCircle2, XCircle,
  Volume2, VolumeX, Truck, Ban,
} from "lucide-react";

const STATUS_BADGE = {
  in_transit: "bg-indigo-100 text-indigo-700 border-indigo-200",
  partially_received: "bg-amber-100 text-amber-700 border-amber-200",
  received: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function TransfersPage() {
  const { has } = useAuth();
  const canSend = has("send_transfer");
  const canReceive = has("receive_transfer");
  const defaultTab = canSend ? "send" : canReceive ? "receive" : "list";
  const [sp, setSp] = useSearchParams();
  const deepLinkList = sp.get("stuck") === "1" || !!sp.get("tstatus");
  const tab = sp.get("tab") || (deepLinkList ? "list" : defaultTab);
  const setTab = (v) => setSp((prev) => {
    const n = new URLSearchParams(prev);
    if (!v || v === defaultTab) n.delete("tab"); else n.set("tab", v);
    return n;
  }, { replace: true });
  const [sites, setSites] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get("/transfers/sites").then(({ data }) => setSites(data)).catch(() => {});
    api.get("/locations").then(({ data }) => setLocations(data.filter((l) => l.type !== "site"))).catch(() => {});
  }, []);

  const tabs = [
    canSend && ["send", "Send", Send],
    canReceive && ["receive", "Receive", PackageCheck],
    ["list", "Transfers", ArrowRightLeft],
  ].filter(Boolean);

  return (
    <div data-testid="transfers-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><ArrowRightLeft className="w-6 h-6 text-[#B01E28]" />Part Transfers</h1>
          <p className="text-sm text-slate-500">Move a part or a full container/truck between your yards with an in-transit handoff. Transfers never affect cost recovery.</p>
        </div>
        <SoundToggle />
      </div>

      {canReceive && (
        <div data-testid="stuck-alert-hint" className="text-xs text-slate-500 mb-4">
          Loads sent but not received in time are flagged in the <b>Task Center</b> and on <b>Floor Home</b>. Owners set the threshold in <b>Notifications</b>.
        </div>
      )}

      <div className="flex rounded-lg border border-slate-200 overflow-hidden w-fit mb-5">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-1.5 text-sm px-4 py-2 ${tab === k ? "bg-[#E0781A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === "send" && <SendTab sites={sites} />}
      {tab === "receive" && <ReceiveTab locations={locations} />}
      {tab === "list" && <ListTab canSend={canSend} />}
    </div>
  );
}

function Flash({ last }) {
  if (!last) return null;
  return (
    <div data-testid="transfer-flash" className={`border-2 rounded-xl p-6 text-center ${last.ok ? "border-emerald-500 bg-emerald-50" : "border-red-500 bg-red-50"}`}>
      {last.ok ? <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" /> : <XCircle className="w-12 h-12 mx-auto text-red-600" />}
      <div className={`mt-2 font-mono text-sm ${last.ok ? "text-emerald-800" : "text-red-700"}`}>{last.message}</div>
    </div>
  );
}

function SendTab({ sites }) {
  const [siteId, setSiteId] = useState("");
  const [name, setName] = useState("");
  const [staged, setStaged] = useState([]);   // batch: scanned part codes
  const [sending, setSending] = useState(false);
  const [last, setLast] = useState(null);
  const [noPhoto, setNoPhoto] = useState(null);   // no tag/photo yet: alert, then the logged bypass

  const doSend = async (code, meta, bypass) => {
    if (!code) return;
    if (!siteId) { toast.error("Pick a destination site."); return; }
    try {
      const { data } = await api.post("/transfers/send", { scan: code, to_site_id: siteId, name: name || null,
                                                           provenance: prov("transfer_send", meta),
                                                           ...(bypass ? { floor_override: true, floor_override_reason: bypass.reason, floor_override_note: bypass.note } : {}) });
      if (!data.ok && data.code === "floor_blocked" && data.override_available) {
        scanSoundOnly(false); setNoPhoto({ ...data, scan: code, meta }); return;   // the dialog IS the plain words
      }
      setNoPhoto(null);
      if (data.ok) scanAccept(data.part?.short_code || "SENT");
      else scanRefuse(data.message || "COULD NOT SEND IT — SCAN IT AGAIN");
      setLast({ ok: data.ok, message: data.message });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      scanRefuse(msg || "COULD NOT SEND IT — SCAN IT AGAIN");
      setLast({ ok: false, message: msg });
    }
  };

  // A batch is staged by scanning each part, then sent in one go. The LAST scan's input is what
  // the batch records — one ledger row covers them all, so it says how they were put in, not
  // which of them was the camera.
  const [stageMeta, setStageMeta] = useState(null);
  const stage = (code, meta) => { setStageMeta(meta); if (code && !staged.includes(code)) setStaged((s) => [...s, code]); };
  const sendBatch = async (bypass) => {
    if (!siteId) { toast.error("Pick a destination site."); return; }
    if (!staged.length) return;
    setSending(true);
    try {
      const { data } = await api.post("/transfers/send-batch", { scans: staged, to_site_id: siteId, name: name || null,
                                                                 provenance: prov("transfer_send", stageMeta),
                                                                 ...(bypass?.reason ? { floor_override: true, floor_override_reason: bypass.reason, floor_override_note: bypass.note } : {}) });
      scanAccept(`${staged.length} SENT`);
      setLast({ ok: true, message: data.message });
      setStaged([]); setName(""); setNoPhoto(null);
      toast.success(data.message);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "batch_blocked" && d.override_available) {
        scanSoundOnly(false); setNoPhoto({ parts: d.floor_parts || [], batch: true }); return;
      }
      scanRefuse(formatApiErrorDetail(d) || "COULD NOT SEND THE LOAD — TAP SEND AGAIN");
      if (d && typeof d === "object" && d.code === "batch_blocked") {
        setLast({ ok: false, message: (d.failures || []).map((f) => `${f.short_code || f.scan}: ${f.why}`).join(" · ") });
      }
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4 max-w-xl">
      <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-4">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-[#B01E28]" />Destination site</Label>
        <Select value={siteId} onValueChange={setSiteId}>
          <SelectTrigger data-testid="send-site-select" className="rounded-lg border-slate-200 mt-2"><SelectValue placeholder="Send to which site?" /></SelectTrigger>
          <SelectContent className="rounded-lg">{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.site_code})</SelectItem>)}</SelectContent>
        </Select>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-3 block">Transfer name (optional)</Label>
        <input data-testid="transfer-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Truck 2 load" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
      </div>

      <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-4">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><ScanLine className="w-3.5 h-3.5 text-[#B01E28]" />Quick send (part or container)</Label>
        {siteId ? (
          <ScanField testid="send-scan" submitLabel="Send" placeholder="Scan part tag or container code" onScan={doSend} continuous paused={!!noPhoto} />
        ) : (
          <p className="text-sm text-slate-400">Pick a destination site first to start scanning.</p>
        )}
      </div>

      <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-4" data-testid="batch-send-card">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><Truck className="w-3.5 h-3.5 text-[#B01E28]" />Batch send (multiple parts, one transfer)</Label>
        {siteId ? (
          <>
            <ScanField testid="batch-scan" submitLabel="Add" placeholder="Scan each part to add to the batch" onScan={stage} continuous paused={!!noPhoto} />
            {staged.length > 0 && (
              <div className="mt-2 border border-slate-100 rounded-lg divide-y divide-slate-100" data-testid="batch-staged">
                {staged.map((c) => (
                  <div key={c} className="flex items-center justify-between px-3 py-1.5 text-sm font-mono">{c}
                    <button onClick={() => setStaged((s) => s.filter((x) => x !== c))} className="text-slate-400 hover:text-red-600 text-xs">remove</button>
                  </div>
                ))}
              </div>
            )}
            <Button data-testid="send-batch-btn" disabled={!staged.length || sending} onClick={() => sendBatch()} className="mt-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 font-bold w-full h-11"><Truck className="w-4 h-4 mr-1" />Send {staged.length} part(s) as one transfer</Button>
          </>
        ) : (
          <p className="text-sm text-slate-400">Pick a destination site first.</p>
        )}
      </div>
      <Flash last={last} />
      <NoPhotoBypassDialog block={noPhoto} onClose={() => setNoPhoto(null)} busy={sending}
        actionLabel="Send anyway — logged"
        onConfirm={(r) => (noPhoto?.batch ? sendBatch(r) : doSend(noPhoto.scan, noPhoto.meta, r))} />
    </div>
  );
}

const REJECT_REASONS = [
  { code: "wrong_parts", label: "Wrong parts" },
  { code: "damaged", label: "Damaged" },
  { code: "not_what_was_sent", label: "Not what was sent" },
  { code: "other", label: "Other" },
];

function ReceiveTab({ locations }) {
  const [bin, setBin] = useState("");
  const [binLabel, setBinLabel] = useState("");
  const [last, setLast] = useState(null);
  const [loadIn, setLoadIn] = useState(false);
  const [siteMode, setSiteMode] = useState(false);
  const [rejReason, setRejReason] = useState(null);
  const [rejNote, setRejNote] = useState("");

  const doReject = async (code) => {
    if (!code || !rejReason) { toast.error("Pick a rejection reason first."); return; }
    if (rejReason === "other" && rejNote.trim().length < 2) { toast.error("Add a note for 'Other'."); return; }
    try {
      const { data } = await api.post("/transfers/reject", { scan: code, reason: rejReason,
        note: rejReason === "other" ? rejNote.trim() : "" });
      if (data.ok) { scanAccept("REJECTED — STAYS AT THE OTHER SITE"); setRejReason(null); setRejNote(""); }
      else scanRefuse(data.message || "COULD NOT REJECT IT");
      setLast({ ok: data.ok, message: data.message });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      scanRefuse(msg || "COULD NOT REJECT IT");
      setLast({ ok: false, message: msg });
    }
  };

  useEffect(() => { api.get("/inventory/load-in-mode").then(({ data }) => setLoadIn(data.enabled)).catch(() => {}); }, []);

  const pickBin = (id) => {
    const l = locations.find((x) => x.id === id);
    setBin(id); setSiteMode(false);
    setBinLabel(l ? `${l.short_code} · ${fullLabel(l)}` : id);
  };

  // PRIMARY path — scan a bin OR a tote/pallet/truck to activate the destination
  const activateByScan = async (code) => {
    if (!code) return;
    try {
      const { data } = await api.get("/locations/resolve", { params: { scan: code } });
      setBin(data.id); setSiteMode(false);
      setBinLabel(`${data.short_code} · ${fullLabel(data)}`);
      scanAccept(`RECEIVING INTO ${data.short_code}`);
      toast.success(`Destination set → ${data.short_code}`);
    } catch (e) {
      scanRefuse(formatApiErrorDetail(e.response?.data?.detail) || "That scan is not a known bin or container.");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That scan is not a known bin or container.");
    }
  };

  const doReceive = async (code, meta) => {
    if (!code) return;
    if (!bin && !siteMode) { toast.error("Set the destination bin first."); return; }
    try {
      const { data } = await api.post("/transfers/receive", {
        scan: code, location_id: bin || undefined, bin_override: siteMode && !bin,
        override_reason: siteMode ? "not binned yet (load-in)" : undefined,
        provenance: prov("transfer_receive", meta),
      });
      if (data.ok) scanAccept(data.part?.short_code || "RECEIVED");
      else scanRefuse(data.message || "COULD NOT RECEIVE IT");
      setLast({ ok: data.ok, message: data.message, noBin: data.received_without_bin });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      scanRefuse(msg || "COULD NOT RECEIVE IT");
      setLast({ ok: false, message: msg });
    }
  };

  return (
    <div className="space-y-4 max-w-xl">
      {/* Destination — scan a bin OR tote/pallet/truck to activate (PRIMARY); dropdown kept as FALLBACK */}
      <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-4">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><MapPin className="w-3.5 h-3.5 text-[#B01E28]" />Destination bin or container (at this site)</Label>
        <ScanField testid="dest-scan" submitLabel="Set"
          placeholder="Scan a bin or tote/pallet/truck to set the destination"
          onScan={activateByScan} />
        {bin && <div data-testid="active-bin" className="mt-2 text-sm font-mono bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">ACTIVE → {binLabel}</div>}
        <details className="mt-3" data-testid="dest-fallback">
          <summary className="text-xs font-mono text-slate-500 cursor-pointer hover:text-slate-700">…or pick manually (fallback)</summary>
          <div className="mt-2">
            <Select value={bin} onValueChange={pickBin}>
              <SelectTrigger data-testid="receive-bin-select" className="rounded-lg border-slate-200"><SelectValue placeholder="Pick a bin / container" /></SelectTrigger>
              <SelectContent className="rounded-lg max-h-72">{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.short_code} · {fullLabel(l)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </details>
        {/* Load-In override — receive the whole load to the site now, bin it later (logged) */}
        {loadIn && !bin && (
          <button data-testid="receive-to-site" onClick={() => setSiteMode((s) => !s)}
            className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${siteMode ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"}`}>
            <PackageCheck className="w-3.5 h-3.5" />{siteMode ? "Load-in: receiving to site (bin later)" : "Receive to site — bin later (load-in)"}
          </button>
        )}
      </div>
      <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-4">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><ScanLine className="w-3.5 h-3.5 text-[#B01E28]" />Scan arriving part, container or truck</Label>
        {siteMode && !bin && <div data-testid="receive-site-banner" className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">Load-in: the whole load will ARRIVE at this site and can be binned later. Every no-bin receive is logged.</div>}
        {(bin || siteMode) ? (
          <ScanField testid="receive-scan" submitLabel="Receive" continuous
            placeholder="Scan the in-transit part / container / truck" onScan={doReceive} />
        ) : (
          <p className="text-sm text-slate-400">Set the destination first{loadIn ? " — or use “Receive to site” above" : ""} to start scanning.</p>
        )}
      </div>

      {/* REJECTED — the load is wrong/damaged: pick a reason, scan the tote, it goes back to origin */}
      <div className="border border-[#B01E28]/30 bg-red-50/40 rounded-xl shadow-sm p-4">
        <Label className="text-xs font-bold uppercase tracking-wider text-[#B01E28] flex items-center gap-1 mb-2"><Ban className="w-3.5 h-3.5" />Rejected — something's wrong, send it back</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {REJECT_REASONS.map((r) => (
            <button key={r.code} data-testid={`reject-reason-${r.code}`} onClick={() => setRejReason(r.code)}
              className={`rounded-full px-4 py-2.5 font-semibold text-sm border-2 transition-colors ${rejReason === r.code ? "border-[#B01E28] bg-red-100 text-[#B01E28]" : "border-slate-200 text-slate-600 hover:border-[#B01E28]"}`}>{r.label}</button>
          ))}
        </div>
        {rejReason === "other" && (
          <input data-testid="reject-note" value={rejNote} onChange={(e) => setRejNote(e.target.value)}
            placeholder="What's wrong?" className="w-full mb-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        )}
        {rejReason ? (
          <ScanField testid="reject-scan" submitLabel="Reject & send back"
            placeholder="Scan the tote / part to reject" onScan={doReject} />
        ) : <p className="text-sm text-slate-400">Pick a reason first, then scan the load to reject it.</p>}
      </div>

      <Flash last={last} />
    </div>
  );
}

// Compact transfer status stepper — same color language: BLUE = transfer/relocate identity
// (sent / in-transit), GREEN = received/done, RED terminal = cancelled. Honestly shows the
// partial middle state (Arriving N/M).
function TransferStepper({ t }) {
  const s = t.status;
  if (s === "cancelled") return <span data-testid={`tstep-${t.transfer_number}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-[#B01E28]"><Ban className="w-3 h-3" />cancelled</span>;
  const received = s === "received";
  const partial = s === "partially_received";
  const nodes = [
    { key: "sent", state: "done", tone: "blue" },
    { key: "transit", state: received ? "done" : "current", tone: "blue" },
    { key: "received", state: received ? "done" : "pending", tone: "green" },
  ];
  const cls = (n) => n.state === "pending" ? "bg-slate-200"
    : n.state === "current" ? "bg-indigo-500 ring-2 ring-indigo-200 animate-pulse"
    : n.tone === "green" ? "bg-emerald-600" : "bg-indigo-500";
  const label = received ? "Received" : partial ? `Arriving ${t.received_count || 0}/${t.item_count}` : "In transit";
  return (
    <div data-testid={`tstep-${t.transfer_number}`}>
      <div className="flex items-center">
        {nodes.map((n, i) => (
          <div key={n.key} className="flex items-center">
            <span className={`w-2.5 h-2.5 rounded-full ${cls(n)}`} />
            {i < nodes.length - 1 && <span className={`h-0.5 w-4 ${nodes[i].state === "done" ? (nodes[i + 1].tone === "green" && nodes[i + 1].state === "done" ? "bg-emerald-500" : "bg-indigo-400") : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>
      <span className={`text-[10px] font-mono uppercase mt-0.5 inline-block ${received ? "text-emerald-700" : "text-indigo-700"}`}>{label}</span>
    </div>
  );
}

function ListTab({ canSend }) {
  const [sp, setSp] = useSearchParams();
  const status = sp.get("tstatus") || "";
  const stuck = sp.get("stuck") === "1";
  const setStatus = (v) => setSp((prev) => {
    const n = new URLSearchParams(prev);
    n.delete("stuck");
    if (!v) n.delete("tstatus"); else n.set("tstatus", v);
    return n;
  }, { replace: true });
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);     // {total, shown, has_more, page} — "showing X of Y"

  // PAGED AND HONEST (his instruction, 2026-10-14): the list used to stop at 500 transfers with
  // nothing on screen saying so. Every page is reachable and the count is always on display.
  const load = (page = 1) => {
    if (page === 1) setRows(null);
    const params = { paged: true, page, page_size: 50, ...(stuck ? { stuck: 1 } : (status ? { status } : {})) };
    api.get("/transfers", { params })
      .then(({ data }) => {
        setRows((prev) => (page === 1 ? (data.items || []) : [...(prev || []), ...(data.items || [])]));
        setMeta(data);
      })
      .catch(() => { setRows([]); setMeta(null); });
  };
  useEffect(() => { load(1); }, [status, stuck]); // eslint-disable-line

  const cancel = async (id) => {
    try { await api.post(`/transfers/${id}/cancel`); toast.success("Transfer cancelled — item returned to origin."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      {stuck && (
        <div data-testid="stuck-filter-banner" className="flex items-center justify-between rounded-lg border border-[#B01E28]/30 bg-red-50 px-4 py-2.5 text-sm text-[#B01E28]">
          <span className="flex items-center gap-1.5 font-semibold"><Ban className="w-4 h-4" />Loads sent but not yet received past the alert threshold — these need someone to receive or reject them.</span>
          <button data-testid="clear-stuck-filter" onClick={() => setStatus("")} className="font-mono text-xs underline hover:no-underline">show all</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden w-fit">
          {[["", "All"], ["in_transit", "In Transit"], ["partially_received", "Partial"], ["received", "Received"], ["cancelled", "Cancelled"]].map(([k, l]) => (
            <button key={k || "all"} data-testid={`filter-${k || "all"}`} onClick={() => setStatus(k)} className={`text-xs font-mono px-3 py-1.5 ${!stuck && status === k ? "bg-[#1F1F1F] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        {meta && (
          <span data-testid="transfers-showing" className="font-mono text-[11px] text-slate-500">
            {meta.total === 0 ? "no transfers"
              : meta.shown >= meta.total ? `showing all ${meta.total}`
                : `showing ${meta.shown} of ${meta.total}`}
          </span>
        )}
      </div>
      <div className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="hidden md:grid grid-cols-[110px_1fr_150px_110px_1fr_90px] gap-2 bg-slate-50 px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <span>Transfer</span><span>Item</span><span>Route</span><span>Status</span><span>Who / when</span><span></span>
        </div>
        {rows === null ? <div className="p-6 text-center font-mono text-xs text-slate-400">LOADING…</div>
          : rows.length === 0 ? <div className="p-6 text-center font-mono text-xs text-slate-400">NO TRANSFERS</div>
          : rows.map((t) => (
            <div key={t.id} data-testid={`transfer-row-${t.transfer_number}`} className="flex flex-col md:grid md:grid-cols-[110px_1fr_150px_110px_1fr_90px] md:gap-2 md:items-center px-4 py-3 border-t border-slate-100 text-sm gap-1">
              <span className="font-mono text-xs font-bold">{t.transfer_number}</span>
              <span className="text-slate-800">{t.kind === "container" ? <Truck className="w-3.5 h-3.5 inline mr-1 text-[#B01E28]" /> : null}{t.target_code} <span className="text-slate-400 text-xs">{t.name || t.target_name}{(t.kind === "container" || t.kind === "parts") ? ` · ${t.received_count || 0}/${t.item_count} item(s)` : ""}</span>
                {t.reject_count > 0 && (
                  <span data-testid={`transfer-rejected-badge-${t.transfer_number}`}
                    className={`ml-1.5 inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-[10px] font-bold align-middle ${t.reject_count >= 2 ? "border-[#B01E28] bg-[#B01E28] text-white" : "border-red-200 bg-red-50 text-[#B01E28]"}`}
                    title={t.rejected_reason_label || "Rejected — heading back to origin"}>
                    <Ban className="w-2.5 h-2.5" />Rejected{t.reject_count >= 2 ? ` ×${t.reject_count}` : ""}
                  </span>
                )}</span>
              <span className="font-mono text-[11px] text-slate-600">{t.from_site_code || "?"} → {t.to_site_code}</span>
              <span><TransferStepper t={t} /></span>
              <span className="font-mono text-[10px] text-slate-500">sent {t.sent_by} {(t.sent_at || "").slice(0, 10)}{t.received_by ? ` · rcvd ${t.received_by}` : ""}
                {t.reject_count > 0 && t.original_sent_at && (
                  <span data-testid={`transfer-first-sent-${t.transfer_number}`} className="block text-slate-400">first sent {(t.original_sent_at || "").slice(0, 10)}</span>
                )}</span>
              <span className="md:text-right">
                {(t.status === "in_transit" || t.status === "partially_received") && canSend && (
                  <Button data-testid={`cancel-${t.transfer_number}`} onClick={() => cancel(t.id)} variant="outline" size="sm" className="rounded-lg border-slate-300 h-7 text-xs"><Ban className="w-3 h-3 mr-1" />Cancel</Button>
                )}
              </span>
            </div>
          ))}
        {meta?.has_more && (
          <button type="button" data-testid="transfers-load-more" onClick={() => load((meta.page || 1) + 1)}
            className="w-full border-t border-slate-100 py-3 font-mono text-xs text-slate-600 hover:text-[#B01E28]">
            Show more — {meta.total - meta.shown} still to come
          </button>
        )}
      </div>
    </div>
  );
}
